import {
  Category,
  CheckInReassignExitSeatsRequest,
  FinnairApisRequirementDocument,
  FinnairApisRequirementKeyEnum,
  FinnairBoundItem,
  FinnairCheckInEligibilityReason,
  FinnairCheckInTravelerEligibility,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { combineServices } from '@fcom/common-booking/modules/ancillaries/utils';
import { SelectOption } from '@fcom/common/interfaces';
import { uniqueBy } from '@fcom/core/utils';

import { ExitRowSeatInfo } from '../interfaces/checkin.interface';
import { CheckInStep } from '../enums';
import { FinnairPassengerItemWithApis } from '../modules/check-in/interface/check-in.interface';

export const UNRECOVERABLE_ELIGIBILITY_REASONS = [
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_FIRST_FLIGHT_ON_OTHER_DEPARTURE_CONTROL_SYSTEM,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_HIGH_PRIORITY_COMMENT,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_ARRIVAL_AIRPORT_INHIBITED,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_DEPARTURE_AIRPORT_INHIBITED,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_CHILD_AGE_TO_BE_CHECKED,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_INFANT_AGE_TO_BE_CHECKED,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_CUSTOMER_ELIGIBILITY_RULE,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_FIRST_FLIGHT_IS_INHIBITED,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_FLIGHT_ELIGIBILITY_DEFAULT_RULE,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_FLIGHT_ELIGIBILITY_RULE,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_ITINERARY_SEQUENCE_CHECK,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_PASSIVE_SEGMENT,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_TICKET_PROBLEM,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_CPR_PROBLEM_AY,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_FLIGHT_GENERALSTATUS,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE,
];

export const GO_TO_AIRLINE_CHECK_IN_REASONS = [
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_FIRST_FLIGHT_OTHER_AIRLINE,
  FinnairCheckInEligibilityReason.NOT_ELIGIBLE_CPR_PROBLEM_OAL,
];

export const hasUnrecoverableEligibilityForBound = (order: FinnairOrder, boundId: string): boolean => {
  return UNRECOVERABLE_ELIGIBILITY_REASONS.includes(order.eligibilities.checkIn?.find((e) => e.id === boundId)?.reason);
};

export const hasGoToAirlineCheckInEligibilityForBound = (order: FinnairOrder, boundId: string): boolean => {
  return GO_TO_AIRLINE_CHECK_IN_REASONS.includes(order.eligibilities.checkIn?.find((e) => e.id === boundId)?.reason);
};

export const hasUnrecoverableEligibilityForPassenger = (
  passengerEligibility: FinnairCheckInTravelerEligibility
): boolean => {
  return UNRECOVERABLE_ELIGIBILITY_REASONS.includes(passengerEligibility?.reason);
};

export const getSeatsFromOrder = (order: FinnairOrder, boundId: string): FinnairServiceItem => {
  const seats = combineServices([order?.services?.included || [], order?.services?.unpaid || []]).find(
    (s) => s.category === Category.SEAT
  );
  return {
    ...seats,
    bounds: seats.bounds.filter((bound) => bound.id === boundId),
  };
};

export const isSeatsReassignmentSuccessful = (
  seats: FinnairServiceItem,
  reassignSeatRequest: CheckInReassignExitSeatsRequest
): boolean => {
  return !Object.keys(reassignSeatRequest.flightsAndTravelers).some((fragmentId) => {
    const passengersForFlights = seats.bounds
      .flatMap((bound) => bound.segments)
      .filter((segment) => segment.id === fragmentId)
      .flatMap((segment) => segment.passengers);
    return reassignSeatRequest.flightsAndTravelers[fragmentId].some(
      (paxId) => passengersForFlights.find((pax) => pax.id === paxId).services.length === 0
    );
  });
};

export const findExitRowSeats = (seats: FinnairServiceItem, passengersCheckingIn: string[]): ExitRowSeatInfo[] => {
  return seats.bounds
    .flatMap((bound) => bound.segments)
    .map((serviceSegment) => {
      return {
        fragmentId: serviceSegment.id,
        passengers: serviceSegment.passengers
          .filter((passenger) => passengersCheckingIn.includes(passenger.id))
          .filter(
            (pax) => (pax.services || []).filter((paxService) => paxService.characteristics.includes('E')).length > 0
          ),
      };
    })
    .filter((exitRowSeatInfo) => exitRowSeatInfo.passengers.length > 0);
};

export const getPaxWithoutExitRowSeatConfirmation = (
  assignedSeats: ExitRowSeatInfo[],
  paxAcceptance: string[]
): ExitRowSeatInfo[] => {
  return assignedSeats
    .map((exitRowSeats) => {
      return {
        ...exitRowSeats,
        passengers: exitRowSeats.passengers.filter((pax) => !paxAcceptance.includes(pax.id)),
      };
    })
    .filter((seatsInfo) => seatsInfo.passengers.length > 0);
};

export const getPassengerIdsWithInfants = (passengers: FinnairPassengerItem[], passengerIds: string[]): string[] => {
  return passengers
    .filter((passenger) =>
      passengerIds.some(
        (passengerId) => passenger.id === passengerId || passenger.accompanyingTravelerId === passengerId
      )
    )
    .map(({ id }) => id);
};

export const getServicesForCheckInPassengers = (
  services: FinnairServiceItem[],
  checkingInPassengersIds: string[]
): FinnairServiceItem[] => {
  return (
    services?.map((service) => {
      return {
        ...service,
        bounds:
          service.bounds?.map((bound) => {
            return {
              ...bound,
              segments:
                bound.segments?.map((segment) => {
                  return {
                    ...segment,
                    passengers:
                      segment.passengers?.filter((passenger) => checkingInPassengersIds.includes(passenger.id)) || [],
                  };
                }) || [],
            };
          }) || [],
      };
    }) || []
  );
};

export const getCheckInStepForPath = (path: string): CheckInStep => {
  if (path.includes(CheckInStep.PASSENGERS)) {
    return CheckInStep.PASSENGERS;
  }
  if (path.includes(CheckInStep.CHILD_SAFETY_NOTICE)) {
    return CheckInStep.CHILD_SAFETY_NOTICE;
  }
  if (path.includes(CheckInStep.APIS)) {
    return CheckInStep.APIS;
  }
  if (path.includes(CheckInStep.SEATMAP)) {
    return CheckInStep.SEATMAP;
  }
  if (path.includes(CheckInStep.KYT)) {
    return CheckInStep.KYT;
  }
  if (path.includes(CheckInStep.RULES)) {
    return CheckInStep.RULES;
  }
  return CheckInStep.CONFIRMATION;
};

export const generateCountryOptions = (
  options: SelectOption[],
  order: FinnairOrder,
  passenger: FinnairPassengerItemWithApis
): SelectOption[] => {
  const separator = {
    name: '-------------------',
    value: '-------------------',
    disabled: true,
  };

  const passportData: FinnairApisRequirementDocument = passenger.apisCpr?.requirements?.find(
    (requirement) => requirement.key === FinnairApisRequirementKeyEnum.PASSPORT
  )?.data as FinnairApisRequirementDocument;

  const FI_COUNTRY_CODE = 'FI';
  const locationCodes =
    order.bounds.length > 1
      ? order.bounds.flatMap((b) => b.departure.locationCode)
      : [order.bounds[0].departure.locationCode, order.bounds[0].arrival.locationCode];
  const locationCountries = locationCodes
    .map((c) => order.locations[c]?.countryCode)
    .filter(Boolean)
    .filter((countryCode) => countryCode !== FI_COUNTRY_CODE);
  const finland = options.find((o) => o.value === FI_COUNTRY_CODE);
  const passengerCountry = options.find((o) => o.value === passportData?.nationality);
  const locationOptions = [
    finland,
    passengerCountry,
    ...locationCountries
      .map((c) => options.find((o) => o.value === c))
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name)),
  ]
    .filter(Boolean)
    .filter(uniqueBy((l) => l.value));

  return [...locationOptions, separator, ...options.filter((o) => !locationOptions.some((l) => l.value === o.value))];
};

export const isNotCarrierConnectBound = (_order: FinnairOrder, _bound: FinnairBoundItem): boolean => {
  // TODO: Temporary disabled
  // !order.eligibilities?.checkIn?.find((boundItem) => boundItem.id === bound.id)?.isCarrierConnect;
  return true;
};
